import { useMutation } from "@tanstack/react-query";
import authService, {
  LoginCredentials,
  RegisterData,
} from "@/services/api/authService";
import type {
  LoginResponse,
  RefreshTokenResponse,
} from "@/services/types/common.types";
import type { LoginResult } from "@/services/types/auth.types";
import type { MutationOptions } from "@/lib/react-query";
import TokenManager from "@/services/api/tokenManager";
import { useAuthStore } from "@/store/slices/authStore";

/**
 * Login mutation.
 * 1. POST login -> tokens are stored in TokenManager (so the interceptor attaches the Bearer token).
 * 2. Immediately GET /me for the user (role, name, ...).
 * 3. onSuccess: sync user + isAuthenticated to Zustand (only after /me succeeded).
 * If /me fails, tokens are cleared so there is no half-logged-in state.
 */
export function useLogin(
  options?: MutationOptions<LoginResult, LoginCredentials>,
) {
  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      await authService.login(credentials);

      try {
        // skipRefresh: the token was just issued, a 401 here is a real failure
        // /me has no email, so keep the one typed in the login form
        const user = await authService.getCurrentUser({
          skipRefresh: true,
          email: credentials.email,
        });
        return {
          success: true,
          data: { user },
        };
      } catch (error) {
        TokenManager.clearTokens();
        throw error;
      }
    },
    retry: false, // wrong credentials should not retry
    onSuccess: (data) => {
      if (data.data?.user) {
        useAuthStore.getState().setUser(data.data.user);
        useAuthStore.getState().setAuthenticated(true);
      }
    },
    ...options,
  });
}

/**
 * Register mutation. On success: sync user + isAuthenticated to Zustand.
 */
export function useRegister(
  options?: MutationOptions<LoginResponse, RegisterData>,
) {
  return useMutation({
    mutationFn: async (data: RegisterData) => {
      const registerResponse = await authService.register(data);
      return {
        success: true,
        data: registerResponse,
      };
    },
    retry: false, // auth failures (e.g. duplicate email) should not retry
    onSuccess: (data) => {
      if (data.data?.user) {
        useAuthStore.getState().setUser(data.data.user);
        useAuthStore.getState().setAuthenticated(true);
      }
    },
    ...options,
  });
}

/**
 * Refresh token mutation. Tokens are stored by authService; optionally sync auth state.
 */
export function useRefreshToken(
  options?: MutationOptions<RefreshTokenResponse>,
) {
  return useMutation({
    mutationFn: async () => {
      const result = await authService.refreshToken();
      return { success: true, data: result };
    },
    retry: false,
    onSuccess: () => {
      useAuthStore.getState().setAuthenticated(true);
    },
    ...options,
  });
}

/**
 * Logout mutation (API call). On success: clear Zustand, dashboard state, and entire TanStack Query cache (see authStore.logout).
 */
export function useLogout(options?: MutationOptions<void>) {
  return useMutation({
    mutationFn: async () => {
      await authService.logout();
      return { success: true };
    },
    retry: false,
    onSuccess: () => {
      useAuthStore.getState().logout();
    },
    ...options,
  });
}
